// components/Benchmark/BenchmarkBulkUpload.jsx
// Bulk upload of tender close-out records from Excel
import React, { useState, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import XLSX from "xlsx-js-style";
import { Button, Text, Badge, Spinner, Tooltip } from "@fluentui/react-components";
import {
  ArrowDownload24Regular, ArrowUpload24Regular, Dismiss24Regular,
  CheckmarkCircle20Regular, ErrorCircle20Regular, Warning24Regular,
  ArrowLeft24Regular, DocumentBulletList24Regular, DataBarVertical24Regular,
  ArrowTrendingLines24Regular,
} from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import { useMsal } from "@azure/msal-react";
import { useBenchmarkDB } from "../../hooks/useBenchmarkDB";
import { BENCHMARK_CATEGORIES, CATEGORY_PHASE } from "../../services/benchmarkService";
import { SAMPLE_CLOSEOUTS } from "../../services/benchmarkSampleData";
import { usePagination, Pagination } from "../common/Pagination";

const GOLD = "#A66400";
const GOLD_LT = "#C87800";
const NAVY = "#001A3D";

// ── Template definition ───────────────────────────────────────────────────────

const COLUMNS = [
  { header: "Tender Reference*",     field: "tenderRef",          width: 26, required: true },
  { header: "Category*",             field: "category",           width: 22, required: true },
  { header: "Location",              field: "location",           width: 18 },
  { header: "Commodity Group",       field: "commodityGroup",     width: 30 },
  { header: "Service Description*",  field: "serviceDescription", width: 52, required: true },
  { header: "Supplier Name*",        field: "supplierName",       width: 30, required: true },
  { header: "Unit of Measure",       field: "unitOfMeasure",      width: 20 },
  { header: "Currency*",             field: "currency",           width: 11, required: true },
  { header: "Benchmark Rate",        field: "benchmarkRate",      width: 16, numeric: true },
  { header: "Lowest Rate",           field: "lowestRate",         width: 16, numeric: true },
  { header: "Awarded Rate*",         field: "awardedRate",        width: 16, numeric: true, required: true },
  { header: "Cost Saving Achieved",  field: "costSavingAchieved", width: 20, numeric: true },
  { header: "Tender Date*",          field: "tenderDate",         width: 14, required: true },
  { header: "Contract Duration",     field: "contractDuration",   width: 18 },
  { header: "Commercial Terms",      field: "commercialTerms",    width: 44 },
  { header: "Award Justification",   field: "awardJustification", width: 44 },
  { header: "Market Observation",    field: "marketObservation",  width: 48 },
];

// Accept a range of header spellings so a re-typed sheet still imports.
const HEADER_MAP = {};
COLUMNS.forEach((c) => {
  const base = c.header.replace(/\*/g, "").trim().toLowerCase();
  HEADER_MAP[base] = c.field;
  HEADER_MAP[c.header.trim().toLowerCase()] = c.field;
  HEADER_MAP[c.field.toLowerCase()] = c.field;
});
Object.assign(HEADER_MAP, {
  "tender ref": "tenderRef",
  "reference": "tenderRef",
  "tender reference (yyyy-mm-dd)": "tenderRef",
  "description": "serviceDescription",
  "product/service description": "serviceDescription",
  "supplier": "supplierName",
  "vendor": "supplierName",
  "uom": "unitOfMeasure",
  "unit": "unitOfMeasure",
  "previous rate": "benchmarkRate",
  "benchmark/previous rate": "benchmarkRate",
  "benchmark": "benchmarkRate",
  "lowest bid": "lowestRate",
  "awarded": "awardedRate",
  "award rate": "awardedRate",
  "cost saving": "costSavingAchieved",
  "savings": "costSavingAchieved",
  "tender date (yyyy-mm-dd)": "tenderDate",
  "award date": "tenderDate",
  "date": "tenderDate",
  "duration": "contractDuration",
  "terms": "commercialTerms",
  "justification": "awardJustification",
  "observation": "marketObservation",
  "market intelligence": "marketObservation",
});

// ── Excel template builder ────────────────────────────────────────────────────

const BORDER = {
  top:    { style: "thin", color: { rgb: "B4BFCC" } },
  bottom: { style: "thin", color: { rgb: "B4BFCC" } },
  left:   { style: "thin", color: { rgb: "B4BFCC" } },
  right:  { style: "thin", color: { rgb: "B4BFCC" } },
};

const cell = (v, s, t) => ({
  v: v === null || v === undefined ? "" : v,
  t: t || (typeof v === "number" ? "n" : "s"),
  s,
});

const ST = {
  headerReq: {
    font: { name: "Helvetica", sz: 11, bold: true, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "A66400" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: BORDER,
  },
  headerOpt: {
    font: { name: "Helvetica", sz: 11, bold: true, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "001A3D" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: BORDER,
  },
  data: (even) => ({
    font: { name: "Helvetica", sz: 10, color: { rgb: "1A1A2E" } },
    fill: { patternType: "solid", fgColor: { rgb: even ? "FFFFFF" : "FFFBF0" } },
    alignment: { horizontal: "left", vertical: "top", wrapText: true },
    border: BORDER,
  }),
  num: (even) => ({
    font: { name: "Helvetica", sz: 10, bold: true, color: { rgb: "A66400" } },
    fill: { patternType: "solid", fgColor: { rgb: even ? "FFFFFF" : "FFFBF0" } },
    alignment: { horizontal: "right", vertical: "top" },
    border: BORDER,
    numFmt: "#,##0.00",
  }),
  date: (even) => ({
    font: { name: "Helvetica", sz: 10, color: { rgb: "1A1A2E" } },
    fill: { patternType: "solid", fgColor: { rgb: even ? "FFFFFF" : "FFFBF0" } },
    alignment: { horizontal: "center", vertical: "top" },
    border: BORDER,
  }),
  guideH: {
    font: { name: "Helvetica", sz: 12, bold: true, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "001A3D" } },
    alignment: { horizontal: "left", vertical: "center" },
  },
  guideK: {
    font: { name: "Helvetica", sz: 10, bold: true, color: { rgb: "A66400" } },
    alignment: { horizontal: "left", vertical: "top", wrapText: true },
  },
  guideV: {
    font: { name: "Helvetica", sz: 10, color: { rgb: "333333" } },
    alignment: { horizontal: "left", vertical: "top", wrapText: true },
  },
  title: {
    font: { name: "Helvetica", sz: 14, bold: true, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "A66400" } },
    alignment: { horizontal: "center", vertical: "center" },
  },
};

const GUIDE_ROWS = [
  ["Tender Reference*", "Your internal tender or contract number. Must be unique — it identifies the record. e.g. LKL/SCM/LOG/2026/001"],
  ["Category*", `One of: ${BENCHMARK_CATEGORIES.join(", ")}. Anything unrecognised is filed under Other.`],
  ["Location", "Where the service was delivered. e.g. Lagos, Port Harcourt, Otakikpo, Offshore OML 113"],
  ["Commodity Group", "THE MOST IMPORTANT FIELD FOR PRICE INTELLIGENCE. Use the SAME wording every time you tender the same thing — this is how the system matches awards across years to measure inflation. e.g. always \"Marine Vessel Charter\", never \"Vessel Hire\" one year and \"Boat Charter\" the next."],
  ["Service Description*", "Full description of what was procured. Free text."],
  ["Supplier Name*", "The supplier who won the award. Keep spelling consistent so supplier scorecards aggregate correctly."],
  ["Unit of Measure", "The basis of the rate. e.g. Per Day, Per Trip, Per Litre, Per Man-Day, Lumpsum. Rates are only ever compared between awards with the SAME unit."],
  ["Currency*", "NGN, USD or EUR. All rate columns on a row must be in this currency."],
  ["Benchmark Rate", "The previous or budgeted rate you tendered against. Leave blank if there was none. Without this the system cannot measure the saving delivered — it is the single highest-value optional field."],
  ["Lowest Rate", "The lowest rate received in the exercise, whether or not it won."],
  ["Awarded Rate*", "The rate actually awarded. Numbers only — no currency symbols or commas."],
  ["Cost Saving Achieved", "Monetary value of the saving over the contract term. Enter 0 if the rate rose."],
  ["Tender Date*", "Date the tender was awarded, formatted YYYY-MM-DD. e.g. 2026-03-10"],
  ["Contract Duration", "e.g. 12 months, 2 years, One-off"],
  ["Commercial Terms", "Payment terms, mobilisation, bonds, Incoterms."],
  ["Award Justification", "Why this supplier was selected over the others."],
  ["Market Observation", "Market intelligence worth preserving — what drove the price, supply constraints, commentary for the next buyer."],
  ["", ""],
  ["CALCULATED FOR YOU", "Do not add columns for these — the system derives them on upload:"],
  ["% Change", "(Awarded − Benchmark) ÷ Benchmark, computed per row."],
  ["Phase", "1 High Value, 2 Mid Value or 3 Low Value, derived from Category."],
  ["Submitted By", "Your account name and email, captured automatically."],
];

function buildTemplate(withSample) {
  const wb = XLSX.utils.book_new();

  // Sheet 1 — the data sheet
  const aoa = [COLUMNS.map((c) => cell(c.header, c.required ? ST.headerReq : ST.headerOpt))];

  if (withSample) {
    SAMPLE_CLOSEOUTS.forEach((r, i) => {
      const even = i % 2 === 0;
      aoa.push(COLUMNS.map((c) => {
        const v = r[c.field];
        if (c.numeric) return cell(typeof v === "number" ? v : 0, ST.num(even), "n");
        if (c.field === "tenderDate") return cell(v || "", ST.date(even));
        return cell(v || "", ST.data(even));
      }));
    });
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = COLUMNS.map((c) => ({ wch: c.width }));
  ws["!rows"] = [{ hpx: 38 }];
  XLSX.utils.book_append_sheet(wb, ws, "Close-Out Data");

  // Sheet 2 — the field guide
  const guide = [
    [cell("TENDER CLOSE-OUT UPLOAD — FIELD GUIDE", ST.title), cell("", ST.title)],
    [],
    [cell("COLUMN", ST.guideH), cell("WHAT TO ENTER", ST.guideH)],
    ...GUIDE_ROWS.map(([k, v]) => [cell(k, ST.guideK), cell(v, ST.guideV)]),
    [],
    [cell("HOW THE INTELLIGENCE WORKS", ST.guideH), cell("", ST.guideH)],
    [
      cell("Matching", ST.guideK),
      cell("Two awards are treated as the same commodity line only when Category, Commodity Group, Unit of Measure and Currency all match. That is what makes an inflation figure trustworthy rather than a mix of different things averaged together.", ST.guideV),
    ],
    [
      cell("Inflation", ST.guideK),
      cell("Measured like-for-like: only lines awarded in both years are compared. A category needs the same commodity tendered in two different years before any inflation figure appears.", ST.guideV),
    ],
    [
      cell("Confidence", ST.guideK),
      cell("Every estimate carries a High / Good / Fair / Low grade based on how many comparable awards exist and how widely their rates vary. Negotiate differently on a Fair estimate than on a High one.", ST.guideV),
    ],
    [
      cell("Consistency", ST.guideK),
      cell("Inconsistent Commodity Group wording is the one thing that will quietly break this. Agree the naming with the category owner before backfilling history.", ST.guideV),
    ],
  ];

  const wsg = XLSX.utils.aoa_to_sheet(guide);
  wsg["!cols"] = [{ wch: 26 }, { wch: 110 }];
  wsg["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
  wsg["!rows"] = [{ hpx: 34 }];
  XLSX.utils.book_append_sheet(wb, wsg, "Field Guide");

  const name = withSample
    ? "LekOil_TenderCloseOut_Template_WithSampleData.xlsx"
    : "LekOil_TenderCloseOut_Template_Blank.xlsx";
  XLSX.writeFile(wb, name, { cellStyles: true });
  return name;
}

// ── Parsing ───────────────────────────────────────────────────────────────────

function parseFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array", cellDates: true });
        // Prefer a sheet that looks like the data sheet; otherwise take the first.
        const name =
          wb.SheetNames.find((n) => /close.?out|data|benchmark|pricing/i.test(n)) ||
          wb.SheetNames[0];
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], {
          header: 1, raw: false, dateNF: "yyyy-mm-dd", defval: "",
        });
        resolve(rows);
      } catch (err) {
        reject(new Error("Could not read that file: " + err.message));
      }
    };
    reader.onerror = () => reject(new Error("Could not open the file. Check it is not locked by Excel and try again."));
    reader.readAsArrayBuffer(file);
  });
}

function toISODate(v) {
  if (!v) return "";
  if (v instanceof Date && !isNaN(v)) return v.toISOString().slice(0, 10);
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // Excel serial number
  if (/^\d{5}(\.\d+)?$/.test(s)) {
    const d = new Date(Math.round((Number(s) - 25569) * 86400000));
    return isNaN(d) ? "" : d.toISOString().slice(0, 10);
  }
  // dd/mm/yyyy or dd-mm-yyyy
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return isNaN(d) ? "" : d.toISOString().slice(0, 10);
  }
  const d = new Date(s);
  return isNaN(d) ? "" : d.toISOString().slice(0, 10);
}

function toNumber(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  return isFinite(n) ? n : 0;
}

function matchCategory(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return null;
  const exact = BENCHMARK_CATEGORIES.find((c) => c.toLowerCase() === s);
  if (exact) return exact;
  const loose = BENCHMARK_CATEGORIES.find(
    (c) => c.toLowerCase().replace(/[^a-z]/g, "") === s.replace(/[^a-z]/g, ""),
  );
  if (loose) return loose;
  const partial = BENCHMARK_CATEGORIES.find(
    (c) => s.includes(c.toLowerCase()) || c.toLowerCase().includes(s),
  );
  return partial || null;
}

function mapRows(rows) {
  if (!rows.length) return { records: [], unmapped: [] };
  const headers = rows[0].map((h) => String(h || "").trim().toLowerCase());
  const idx = {};
  const unmapped = [];
  headers.forEach((h, i) => {
    if (!h) return;
    const f = HEADER_MAP[h];
    if (f) { if (!(f in idx)) idx[f] = i; }
    else unmapped.push(rows[0][i]);
  });

  const get = (row, field) => String(row[idx[field]] ?? "").trim();

  const records = rows.slice(1)
    .map((row, i) => {
      const rawCat = get(row, "category");
      const category = matchCategory(rawCat);
      const benchmarkRate = toNumber(get(row, "benchmarkRate"));
      const awardedRate = toNumber(get(row, "awardedRate"));
      return {
        _rowNum: i + 2,
        _rawCategory: rawCat,
        tenderRef:          get(row, "tenderRef"),
        category:           category || "Other",
        _categoryMatched:   !!category,
        location:           get(row, "location"),
        commodityGroup:     get(row, "commodityGroup"),
        serviceDescription: get(row, "serviceDescription"),
        supplierName:       get(row, "supplierName"),
        unitOfMeasure:      get(row, "unitOfMeasure"),
        currency:           (get(row, "currency") || "NGN").toUpperCase(),
        benchmarkRate,
        lowestRate:         toNumber(get(row, "lowestRate")),
        awardedRate,
        costSavingAchieved: toNumber(get(row, "costSavingAchieved")),
        percentageChange:   benchmarkRate > 0 ? ((awardedRate - benchmarkRate) / benchmarkRate) * 100 : 0,
        tenderDate:         toISODate(get(row, "tenderDate")),
        contractDuration:   get(row, "contractDuration"),
        commercialTerms:    get(row, "commercialTerms"),
        awardJustification: get(row, "awardJustification"),
        marketObservation:  get(row, "marketObservation"),
        phase:              CATEGORY_PHASE[category || "Other"] || 3,
      };
    })
    .filter((r) => r.tenderRef || r.supplierName || r.awardedRate > 0);

  return { records, unmapped };
}

function validate(r, existingRefs, seenRefs) {
  const errors = [];
  const warnings = [];

  if (!r.tenderRef) errors.push("Tender Reference is required");
  if (!r.serviceDescription) errors.push("Service Description is required");
  if (!r.supplierName) errors.push("Supplier Name is required");
  if (!r.awardedRate || r.awardedRate <= 0) errors.push("Awarded Rate must be a number greater than zero");
  if (!r.tenderDate) errors.push("Tender Date is required — use YYYY-MM-DD");
  else if (new Date(r.tenderDate) > new Date(Date.now() + 86400000 * 365)) {
    errors.push(`Tender Date ${r.tenderDate} is more than a year in the future`);
  }
  if (!["NGN", "USD", "EUR"].includes(r.currency)) {
    errors.push(`Currency "${r.currency}" is not one of NGN, USD, EUR`);
  }

  if (r.tenderRef && existingRefs.has(r.tenderRef.toLowerCase())) {
    warnings.push("A record with this Tender Reference already exists in the database");
  }
  if (r.tenderRef && seenRefs.has(r.tenderRef.toLowerCase())) {
    warnings.push("This Tender Reference appears more than once in the file");
  }
  if (r._rawCategory && !r._categoryMatched) {
    warnings.push(`Category "${r._rawCategory}" was not recognised — filed under Other`);
  }
  if (!r.commodityGroup) {
    warnings.push("No Commodity Group — this award cannot be price-compared against other years");
  }
  if (!r.unitOfMeasure) {
    warnings.push("No Unit of Measure — reduces comparability");
  }
  if (!r.benchmarkRate) {
    warnings.push("No Benchmark Rate — saving and inflation cannot be measured for this award");
  }
  if (r.lowestRate > 0 && r.awardedRate > 0 && r.lowestRate > r.awardedRate) {
    warnings.push("Lowest Rate is higher than Awarded Rate — check the figures");
  }

  if (r.tenderRef) seenRefs.add(r.tenderRef.toLowerCase());
  return { ...r, _errors: errors, _warnings: warnings };
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BenchmarkBulkUpload() {
  const { canEdit, isStaff, isEditor, isAdmin } = useUserAccess();
  const { accounts } = useMsal();
  const { records: existing, addRecords, isDemoMode } = useBenchmarkDB();
  const navigate = useNavigate();

  const currentEmail = accounts[0]?.username || "";
  const currentName = accounts[0]?.name || "SCM User";
  const canUpload = canEdit || isStaff || isEditor || isAdmin;

  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState(null);
  const [showAllWarnings, setShowAllWarnings] = useState(false);
  const fileRef = useRef(null);

  const existingRefs = useMemo(
    () => new Set(existing.map((r) => (r.tenderRef || "").toLowerCase()).filter(Boolean)),
    [existing],
  );

  const validCount = rows.filter((r) => r._errors.length === 0).length;
  const invalidCount = rows.length - validCount;
  const warnCount = rows.filter((r) => r._errors.length === 0 && r._warnings.length > 0).length;

  const preview = usePagination(rows, { defaultPageSize: 100, resetKey: file?.name || "" });

  if (!canUpload) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Warning24Regular style={{ fontSize: 44, color: "#D13438", marginBottom: 12 }} />
        <Text size={500} weight="semibold" block style={{ marginBottom: 8, color: NAVY }}>Access Restricted</Text>
        <Text style={{ color: "#888" }}>Staff access or above is required to upload close-out records.</Text>
      </div>
    );
  }

  async function handleFileSelect(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setRows([]);
    setUnmapped([]);
    setParsing(true);
    setParseError("");
    try {
      const raw = await parseFile(f);
      const { records, unmapped: um } = mapRows(raw);
      if (!records.length) {
        setParseError(
          "No close-out rows were found. Check that the first row holds the column headings and that there is at least one row of data beneath it.",
        );
        return;
      }
      const seen = new Set();
      setRows(records.map((r) => validate(r, existingRefs, seen)));
      setUnmapped(um);
    } catch (err) {
      setParseError(err.message);
    } finally {
      setParsing(false);
    }
  }

  async function handleUpload() {
    const valid = rows.filter((r) => r._errors.length === 0);
    if (!valid.length) return;
    setUploading(true);
    setProgress(0);
    setTotal(valid.length);
    try {
      const payload = valid.map(({ _rowNum, _errors, _warnings, _rawCategory, _categoryMatched, ...rest }) => rest);
      const res = await addRecords(payload, {
        submittedBy: currentEmail,
        submittedByName: currentName,
        onProgress: (done, tot) => { setProgress(done); setTotal(tot); },
      });
      setResult(res);
    } catch (err) {
      setResult({ created: [], failed: [{ error: err.message }] });
    } finally {
      setUploading(false);
    }
  }

  function reset() {
    setFile(null);
    setRows([]);
    setUnmapped([]);
    setResult(null);
    setParseError("");
    setProgress(0);
    setTotal(0);
    setShowAllWarnings(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto" }}>
      <div style={{ marginBottom: 18 }}>
        <Link to="/benchmark" style={{ textDecoration: "none", color: GOLD_LT, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <ArrowLeft24Regular style={{ fontSize: 16 }} />Back to Benchmark Database
        </Link>
      </div>

      <div style={{ marginBottom: 26, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: `linear-gradient(135deg,${GOLD},${GOLD_LT})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <ArrowUpload24Regular style={{ color: "#fff", fontSize: 21 }} />
        </div>
        <div>
          <Text block size={700} weight="bold" style={{ color: NAVY, marginBottom: 2 }}>Bulk Close-Out Upload</Text>
          <Text style={{ color: "#888", fontSize: 14 }}>
            Load historical tender close-outs from Excel — the fastest way to build the price intelligence base.
            {isDemoMode && <Badge appearance="tint" color="warning" style={{ marginLeft: 10 }}>Demo Mode — saved locally</Badge>}
          </Text>
        </div>
      </div>

      {/* ── Step 1 ── */}
      <div style={card}>
        <Step n={1} label="Start from the template" />
        <Text style={{ color: "#555", fontSize: 13, display: "block", marginBottom: 16, lineHeight: 1.7 }}>
          The template ships with <strong>{SAMPLE_CLOSEOUTS.length} worked example close-outs</strong> covering every category —
          real commodity lines repeated across 2025 and 2026 so you can see exactly what the intelligence engine needs.
          Upload it as-is to explore the system, or clear the rows and enter your own.
          A <strong>Field Guide</strong> sheet explains every column.
        </Text>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
          {COLUMNS.map((c) => (
            <span key={c.field} style={{
              fontSize: 11, padding: "3px 9px", borderRadius: 4,
              border: `1px solid ${c.required ? GOLD : "#E4E8EF"}`,
              color: c.required ? GOLD : "#666",
              background: c.required ? "#FFF8EE" : "#FAFAF9",
              fontWeight: c.required ? 600 : 400,
            }}>
              {c.header}
            </span>
          ))}
        </div>

        <div style={{ background: "#FFFBF2", border: "1px solid #FFE8B8", borderRadius: 9, padding: "12px 15px", marginBottom: 18 }}>
          <Text style={{ fontSize: 12.5, color: "#5A4200", lineHeight: 1.7 }}>
            <strong>The one field that matters most:</strong> keep <strong>Commodity Group</strong> worded identically every time you
            tender the same thing. "Marine Vessel Charter" in 2025 and "Vessel Hire" in 2026 look like two unrelated commodities to
            the engine, and no inflation can be measured between them. Everything else tolerates variation; this does not.
          </Text>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Button icon={<ArrowDownload24Regular />} appearance="primary"
            onClick={() => buildTemplate(true)}
            style={{ background: `linear-gradient(135deg,${GOLD},${GOLD_LT})` }}>
            Download Template with Sample Data
          </Button>
          <Button icon={<ArrowDownload24Regular />} appearance="secondary" onClick={() => buildTemplate(false)}>
            Download Blank Template
          </Button>
        </div>
      </div>

      {/* ── Step 2 ── */}
      <div style={card}>
        <Step n={2} label="Upload your file" />
        {!file && (
          <div
            style={{
              border: "2px dashed #D8C9A8", borderRadius: 10, padding: "44px 24px",
              textAlign: "center", cursor: "pointer", background: "#FFFDF8",
            }}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files?.[0]) handleFileSelect({ target: { files: e.dataTransfer.files } });
            }}
          >
            <ArrowUpload24Regular style={{ fontSize: 44, color: GOLD_LT, marginBottom: 10 }} />
            <Text block weight="semibold" style={{ color: NAVY, marginBottom: 4 }}>Click to browse or drag a file here</Text>
            <Text style={{ color: "#888", fontSize: 12 }}>Accepts .xlsx and .xls</Text>
          </div>
        )}

        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleFileSelect} />

        {parsing && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 16, color: "#555" }}>
            <Spinner size="small" /><Text>Reading the file…</Text>
          </div>
        )}

        {parseError && (
          <div style={{ display: "flex", gap: 10, padding: "12px 16px", background: "#FDE7E9", borderRadius: 8, color: "#C4262E", fontSize: 13, marginTop: 12, lineHeight: 1.6 }}>
            <Warning24Regular style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{parseError}</span>
          </div>
        )}

        {file && !parsing && (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#F5F3EF", borderRadius: 8 }}>
            <DocumentBulletList24Regular style={{ color: GOLD_LT, flexShrink: 0 }} />
            <Text style={{ flex: 1, fontSize: 13 }}>{file.name}</Text>
            <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} onClick={reset} disabled={uploading} />
          </div>
        )}

        {unmapped.length > 0 && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: "#F0F4FA", border: "1px solid #D6E2F0", borderRadius: 8 }}>
            <Text style={{ fontSize: 12, color: "#44536A", lineHeight: 1.6 }}>
              These columns were not recognised and will be ignored: <strong>{unmapped.join(", ")}</strong>.
              Everything else imported normally.
            </Text>
          </div>
        )}
      </div>

      {/* ── Step 3 ── */}
      {rows.length > 0 && !result && (
        <div style={card}>
          <Step n={3} label="Check and commit" />

          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <Pill bg="#DFF6DD" color="#107C10">{validCount} ready to upload</Pill>
            {warnCount > 0 && <Pill bg="#FFF4EC" color="#C4762A">{warnCount} with warnings — will still upload</Pill>}
            {invalidCount > 0 && <Pill bg="#FDE7E9" color="#C4262E">{invalidCount} blocked — will be skipped</Pill>}
          </div>

          {uploading && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <Text style={{ fontSize: 13, color: "#555" }}>Saving close-out records…</Text>
                <Text style={{ fontSize: 13, fontWeight: 600 }}>{progress} / {total}</Text>
              </div>
              <div style={{ height: 8, background: "#EDEBE9", borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  height: "100%", width: total > 0 ? `${(progress / total) * 100}%` : "0%",
                  background: `linear-gradient(90deg,${GOLD},${GOLD_LT})`, borderRadius: 4, transition: "width 0.3s ease",
                }} />
              </div>
            </div>
          )}

          <div style={{ overflowX: "auto", maxHeight: 440, overflowY: "auto", marginBottom: 16, border: "1px solid #EDEBE9", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr style={{ background: NAVY }}>
                  {["ROW", "TENDER REF", "CATEGORY", "COMMODITY GROUP", "SUPPLIER", "UNIT", "AWARDED", "% CHG", "DATE", ""].map((h) => (
                    <th key={h} style={{ padding: "9px 10px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, textAlign: h === "AWARDED" || h === "% CHG" ? "right" : "left", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.pageItems.map((r, i) => {
                  const bad = r._errors.length > 0;
                  const warn = !bad && r._warnings.length > 0;
                  const pct = r.benchmarkRate > 0 ? r.percentageChange : null;
                  return (
                    <tr key={r._rowNum} style={{
                      background: bad ? "#FFF6F6" : warn ? "#FFFBF2" : i % 2 ? "#FAFAF9" : "#fff",
                      borderBottom: "1px solid #EEF0F4",
                    }}>
                      <td style={cellS({ color: "#AAA", fontSize: 11 })}>{r._rowNum}</td>
                      <td style={cellS({ fontWeight: 600, color: r.tenderRef ? NAVY : "#C4262E", minWidth: 150 })}>
                        {r.tenderRef || "(missing)"}
                      </td>
                      <td style={cellS({ minWidth: 120 })}>
                        <span style={{ color: r._categoryMatched ? "#333" : "#C4762A" }}>{r.category}</span>
                      </td>
                      <td style={cellS({ minWidth: 160, color: r.commodityGroup ? "#333" : "#C4762A" })}>
                        {r.commodityGroup || "(none)"}
                      </td>
                      <td style={cellS({ minWidth: 140, color: r.supplierName ? "#333" : "#C4262E" })}>
                        {r.supplierName || "(missing)"}
                      </td>
                      <td style={cellS({ minWidth: 90, color: r.unitOfMeasure ? "#555" : "#C4762A", fontSize: 11 })}>
                        {r.unitOfMeasure || "(none)"}
                      </td>
                      <td style={cellS({ textAlign: "right", fontWeight: 700, color: r.awardedRate > 0 ? GOLD : "#C4262E", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" })}>
                        {r.awardedRate > 0 ? `${r.currency} ${r.awardedRate.toLocaleString("en-NG")}` : "(missing)"}
                      </td>
                      <td style={cellS({ textAlign: "right", whiteSpace: "nowrap" })}>
                        {pct === null ? <span style={{ color: "#CCC" }}>—</span> : (
                          <span style={{ color: pct > 0 ? "#C4262E" : pct < 0 ? "#107C10" : "#666", fontWeight: 700 }}>
                            {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
                          </span>
                        )}
                      </td>
                      <td style={cellS({ color: r.tenderDate ? "#555" : "#C4262E", whiteSpace: "nowrap" })}>
                        {r.tenderDate || "(missing)"}
                      </td>
                      <td style={cellS({})}>
                        {bad ? (
                          <Tooltip relationship="label" content={r._errors.join(" · ")}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, color: "#C4262E", cursor: "help" }}>
                              <ErrorCircle20Regular /><span style={{ fontSize: 10 }}>{r._errors.length}</span>
                            </span>
                          </Tooltip>
                        ) : warn ? (
                          <Tooltip relationship="label" content={r._warnings.join(" · ")}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, color: "#C4762A", cursor: "help" }}>
                              <Warning24Regular style={{ fontSize: 16 }} /><span style={{ fontSize: 10 }}>{r._warnings.length}</span>
                            </span>
                          </Tooltip>
                        ) : (
                          <CheckmarkCircle20Regular style={{ color: "#107C10" }} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {preview.isPaged && (
            <div style={{ border: "1px solid #EDEBE9", borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
              <Pagination {...preview} accent={GOLD} noun="row" />
            </div>
          )}

          {(invalidCount > 0 || warnCount > 0) && (
            <div style={{ marginBottom: 16 }}>
              <Button appearance="subtle" size="small" onClick={() => setShowAllWarnings((s) => !s)} style={{ color: GOLD }}>
                {showAllWarnings ? "Hide" : "Show"} all issues in a list
              </Button>
              {showAllWarnings && (
                <div style={{ marginTop: 10, maxHeight: 240, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                  {rows.filter((r) => r._errors.length || r._warnings.length).slice(0, 200).map((r) => (
                    <div key={r._rowNum} style={{
                      padding: "9px 13px", borderRadius: 7, fontSize: 12, lineHeight: 1.6,
                      background: r._errors.length ? "#FFF6F6" : "#FFFBF2",
                      border: `1px solid ${r._errors.length ? "#F5C2C7" : "#FFE8B8"}`,
                    }}>
                      <strong style={{ color: NAVY }}>Row {r._rowNum}</strong>
                      {r.tenderRef && <span style={{ color: "#777" }}> · {r.tenderRef}</span>}
                      {r._errors.map((e, k) => (
                        <div key={`e${k}`} style={{ color: "#C4262E" }}>✕ {e}</div>
                      ))}
                      {r._warnings.map((w, k) => (
                        <div key={`w${k}`} style={{ color: "#8A5900" }}>⚠ {w}</div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button appearance="secondary" onClick={reset} disabled={uploading}>Clear and start over</Button>
            <Button
              appearance="primary"
              icon={uploading ? <Spinner size="tiny" /> : <ArrowUpload24Regular />}
              onClick={handleUpload}
              disabled={uploading || validCount === 0}
              style={{ background: `linear-gradient(135deg,${GOLD},${GOLD_LT})`, minWidth: 190 }}
            >
              {uploading ? `Saving ${progress}/${total}…` : `Upload ${validCount} record${validCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}

      {/* ── Result ── */}
      {result && (
        <div style={card}>
          <Text weight="semibold" size={400} block style={{ marginBottom: 16, color: NAVY }}>Upload complete</Text>
          <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ padding: "12px 24px", borderRadius: 10, background: "#DFF6DD", color: "#107C10", fontWeight: 700, fontSize: 16 }}>
              {result.created.length} saved
            </div>
            {result.failed.length > 0 && (
              <div style={{ padding: "12px 24px", borderRadius: 10, background: "#FDE7E9", color: "#C4262E", fontWeight: 700, fontSize: 16 }}>
                {result.failed.length} failed
              </div>
            )}
          </div>

          {result.failed.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <Text weight="semibold" style={{ color: "#C4262E", fontSize: 13 }} block>What failed:</Text>
              {result.failed.map((f, i) => (
                <div key={i} style={{ padding: "7px 13px", background: "#FFF6F6", borderRadius: 6, marginTop: 5, fontSize: 12, color: "#C4262E" }}>
                  {f.item?.tenderRef || `Record ${i + 1}`}: {f.error}
                </div>
              ))}
            </div>
          )}

          {result.created.length > 0 && (
            <div style={{ background: "#FFFBF2", border: "1px solid #FFE8B8", borderRadius: 9, padding: "13px 16px", marginBottom: 20 }}>
              <Text style={{ fontSize: 12.5, color: "#5A4200", lineHeight: 1.7 }}>
                The intelligence engine has already picked these up. Inflation, price movement and should-cost estimates
                are recalculated from the full dataset every time it loads — open Market Intelligence to see what the new records changed.
              </Text>
            </div>
          )}

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Button appearance="secondary" onClick={reset}>Upload another file</Button>
            <Button appearance="secondary" icon={<DataBarVertical24Regular />} onClick={() => navigate("/benchmark")}>
              View the database
            </Button>
            <Button appearance="primary" icon={<ArrowTrendingLines24Regular />}
              onClick={() => navigate("/benchmark/intelligence")}
              style={{ background: `linear-gradient(135deg,${GOLD},${GOLD_LT})` }}>
              Open Market Intelligence
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Small bits ────────────────────────────────────────────────────────────────

const card = {
  background: "#fff", borderRadius: 12, border: "1px solid #EDEBE9",
  padding: 24, marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};

const cellS = (extra) => ({ padding: "7px 10px", verticalAlign: "top", ...extra });

function Pill({ bg, color, children }) {
  return (
    <span style={{ padding: "6px 14px", borderRadius: 20, background: bg, color, fontWeight: 600, fontSize: 13 }}>
      {children}
    </span>
  );
}

function Step({ n, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <div style={{
        width: 28, height: 28, borderRadius: "50%", background: GOLD_LT, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 700, fontSize: 13, flexShrink: 0,
      }}>{n}</div>
      <Text weight="semibold" size={400} style={{ color: NAVY }}>{label}</Text>
    </div>
  );
}
